import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card";
import { DictType } from "@pma/types";
import useTranslator from "@hooks/use-translator";
import { useMemo } from "react";
import { format, formatDistance } from "date-fns";
import { he } from "date-fns/locale";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@components/common/accordion";
import { Progress } from "@components/common/progress";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@components/common/hover-card";
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar";
import { Badge } from "@components/common/badge";
import { Clock, Users, HardDrive, CheckCircle2, Circle } from "lucide-react";
import ProjectEditor from "./project-editor";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/common/tooltip";

import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes";
import { cn } from "@pma/utils/helpers";
import { Button } from "@components/common/button";
import { ScrollArea } from "@components/common/scroll-area";
import { useAuth } from "@stores/auth-store";
import { getUserandTask, groupTaskByStatus } from "@pma/utils/utils";
import axios from "axios";
import {
  SERVER_URL,
  TASK_BY_CURRENT_USER_ENDPOINT,
} from "@pma/utils/api-routes";
import { parseCookies } from "nookies";
import { ACCESS_TOKEN } from "@pma/utils/constants";
import { useQuery } from "@tanstack/react-query";

interface Props {
  project: DictType;
}

const ProjectSettings = ({ project }: Props) => {
  const { translate, locale } = useTranslator();
  const { authConfig } = useAuth();

  const navigate = useNavigate();
  const [searchParam] = useSearchParams();
  const newParams = new URLSearchParams(searchParam); 

  const isHebrew = locale === "he";

  const orgId = authConfig?.last_organization_id;

  const accessToken = parseCookies()[ACCESS_TOKEN];

  const revalidateProject = () => {
    newParams.set('revalidate', 'projects');
    navigate(`?${newParams.toString()}`);
  };

  const members = project?.members_id;

  const getProjectTasks = async () => {
    const response = await axios.post(
      SERVER_URL + TASK_BY_CURRENT_USER_ENDPOINT,
      {
        project_id: project?.id,
        show_created_by: true,
        organization_id: orgId,
        paging_per_page: 50,
        show_deleted: false
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,

          "Content-Type": "application/json",
        },
      },
    );
    return response.data;
  };

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["get-tasks", orgId, project?.id],
    queryFn: getProjectTasks,
  });

  const projectTasks = data?.items || [];

  const groupedTasks = getUserandTask(projectTasks);
  const tasksByStatus = groupTaskByStatus(projectTasks);

  const totalFileSize = useMemo(() => {
    return (
      project?.linked_files_id?.reduce((total: any, item: any) => {
        return total + item.size;
      }, 0) ?? 0
    );
  }, [project]);

  return (
    <Card
      className={cn(
        "space-y-3 my-6 border-none shadow-none",
        isHebrew ? "rtl" : "ltr",
      )}
    >
      <CardHeader className="pb-0 mb-0">
        <CardTitle className="flex justify-between items-center">
          <span>{project.title}</span>
          <Badge variant="secondary" className="capitalize">
            {isHebrew
              ? project?.type?.project_type
              : project?.type?.project_type_en}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <ProjectEditor
          refetch={revalidateProject}
          id={project?.id}
          title={"description"}
          value={project?.description}
          maxLength={200}
        >
          <p
            className={cn(
              "text-sm text-muted-foreground",
              isHebrew ? "text-right" : "text-left",
            )}
          >
            {project.description || "---"}
          </p>
        </ProjectEditor>
        <div className="flex items-center space-x-4 text-sm">
          <Tooltip>
            <TooltipTrigger className="flex items-center">
              <Clock className="w-4 h-4 mx-2" />
              {translate("Created")}:
              {project?.created_at &&
                formatDistance(project?.created_at, new Date(), {
                  locale: isHebrew ? he : undefined,
                  addSuffix: true,
                })}{" "}
            </TooltipTrigger>
            <TooltipContent>
              {project?.created_at &&
                format(project?.created_at, "LLL dd, y", {
                  locale: isHebrew ? he : undefined,
                })}
            </TooltipContent>
          </Tooltip>
          <div className="flex items-center">
            <Clock className="w-4 h-4 mx-2" />
            <span>
              {translate("Updated")}:
              {project?.last_update
                ? format(project?.last_update, "LLL dd, y", {
                    locale: isHebrew ? he : undefined,
                  })
                : format(project?.created_at, "LLL dd, y", {
                    locale: isHebrew ? he : undefined,
                  })}
            </span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="flex items-center">
              <HardDrive className="w-4 h-4 mx-2" />
              {translate("Storage usage")}
            </span>
            <span>
              {parseFloat((totalFileSize / Math.pow(2, 23)).toFixed(2))}MB
            </span>
          </div>
          <Progress value={totalFileSize * 100} />
        </div>
        <div className="flex items-center space-x-2">
          <Users className="w-4 h-4" />
          <div className="flex -space-x-2">
            {members?.map((member: DictType, index: number) => (
              <Link to={`${USERS_PAGE_ROUTE}/${member.id}`} key={index}>
                <Tooltip>
                  <TooltipTrigger>
                    <Avatar className="w-8 h-8 border-2 border-background">
                      <AvatarImage
                        src={member?.user_picture?.url}
                        alt={member.name}
                      />
                      <AvatarFallback>
                        {member.name
                          .split(" ")
                          .map((n: any) => n[0])
                          .join("")}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>{member.name}</TooltipContent>
                </Tooltip>
              </Link>
            ))}
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="tasks-by-status">
            <AccordionTrigger>{translate("Tasks by Status")}</AccordionTrigger>
            <AccordionContent>
              <TaskList tasks={tasksByStatus} groupBy="status" />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="tasks-by-assignee">
            <AccordionTrigger>
              {translate("Tasks by Assignees")}
            </AccordionTrigger>
            <AccordionContent>
              <TaskList tasks={groupedTasks} groupBy="assignee" />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </CardContent>
      <CardFooter className="justify-end">
        <p className="text-xs text-muted-foreground">
          {translate("Last updated")}&nbsp;
          {project?.last_update
            ? format(project?.last_update, "LLL dd, y", {
                locale: isHebrew ? he : undefined,
              })
            : format(project?.created_at, "LLL dd, y", {
                locale: isHebrew ? he : undefined,
              })}
        </p>
      </CardFooter>
    </Card>
  );
};

export default ProjectSettings;

const TaskList = ({
  tasks,
  groupBy,
}: {
  tasks: any[];
  groupBy: "assignee" | "status";
}) => {
  const { locale } = useTranslator();
  const pathname = useLocation()?.pathname;
  const navigate = useNavigate();
  const { setActiveTab } = useAuth();

  const isHebrew = locale === "he";
  const handleViewTask = (taskId: string) => {
    navigate(pathname + `?task=${taskId}`);
    setActiveTab("table");
  };

  return (
    <div className="space-y-4">
      <div>
        <ul className="space-y-1">
          {tasks.map((task: DictType) => (
            <li key={task.id} className="flex items-center text-sm">
              {groupBy === "status" ? (
                <HoverCard>
                  <HoverCardTrigger className="flex items-center">
                    {task.statuses?.finished ||
                    task?.statuses?.name
                      .toLocaleLowerCase()
                      .includes("complete") ||
                    task?.statuses?.name
                      .toLocaleLowerCase()
                      .includes("done") ? (
                      <CheckCircle2 className="w-4 h-4 mx-2 text-green-500" />
                    ) : (
                      <Circle className="w-4 h-4 mx-2 text-yellow-500" />
                    )}
                    <span>
                      {task?.statuses?.name} - {task?.tasks?.length}
                    </span>
                  </HoverCardTrigger>
                  <HoverCardContent className="flex flex-col justify-start items-start space-y-2 md:min-w-[350px] w-full">
                    <ScrollArea className="max-h-[60vh]">
                      {task?.tasks?.map((t: DictType, index: number) => (
                        <div
                          className={cn(
                            "flex gap-0.5 items-center",
                            isHebrew ? "rtl" : "ltr",
                          )}
                          key={t?.id}
                        >
                          <small>{index + 1}.</small>
                          <Button
                            onClick={() => handleViewTask(t?.id)}
                            variant={"link"}
                            className="p-0 m-0 text-black hover:text-primary"
                          >
                            <span>{t?.title}</span>{" "}
                          </Button>
                        </div>
                      ))}
                    </ScrollArea>
                  </HoverCardContent>
                </HoverCard>
              ) : (
                <HoverCard>
                  <HoverCardTrigger>
                    <div className="flex gap-2 items-center">
                      <Avatar className="w-8 h-8 border-2 border-background">
                        <AvatarImage
                          src={task?.user?.url}
                          alt={task?.user.name}
                        />
                        <AvatarFallback>
                          {task?.user.name
                            .split(" ")
                            .map((n: any) => n[0])
                            .join("")}
                        </AvatarFallback>
                      </Avatar>
                      <span>
                        {task?.user.name} - {task?.tasks?.length}
                      </span>{" "}
                    </div>
                  </HoverCardTrigger>
                  <HoverCardContent className="flex flex-col justify-start items-start space-y-2 md:min-w-[350px] w-full">
                    <ScrollArea className="max-h-[60vh]">
                      {task?.tasks?.map((t: DictType, index: number) => (
                        <div
                          className={cn(
                            "flex gap-0.5 items-center",
                            isHebrew ? "rtl" : "ltr",
                          )}
                          key={t?.id}
                        >
                          <small>{index + 1}.</small>
                          <Button
                            onClick={() => handleViewTask(t?.id)}
                            variant={"link"}
                            className="p-0 m-0 text-black hover:text-primary"
                          >
                            <span>{t?.title}</span>
                          </Button>
                        </div>
                      ))}
                    </ScrollArea>
                  </HoverCardContent>
                </HoverCard>
              )}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
